import { TreeViewDataItem } from "@patternfly/react-core";
import { isArray, maxBy, isObject, isString, forEach, groupBy, get, trim, isEmpty } from "lodash-es";
import hash from 'object-hash';
import { ReactNode } from "react";
import { v4 as uuidv4 } from 'uuid';

import { assertExpectedProps } from "../util/expected-prop";
import { naturalSort } from "../util/sort";
import { AeTitleRule, CustomizationTreeViewDataItem, DEFAULT_MODEL_CUSTOMIZATION_NAME, DEFAULT_SUBSCRIPTION, DicomAttributeRule, DicomRule, DICOM_RULE_TRIGGER_ACTION, FmaId, GlobalRoiCustomization, isAeTitleRule, isDicomRule, METADATA_FILENAME_ATTRIBUTE, ModelCustomizationBase, ModelCustomizationEntities, ModelCustomizationEntitiesForExport, ModelCustomizationMetadataItem, ModelCustomizationOutput, ModelCustomizationOutputEntities, ModelTrigger, ModelVisibility, PhysicalProperty, PHYSICAL_PROPERTY_REL_ELEC_DENSITY, PHYSICAL_PROPERTY_REL_MASS_DENSITY, RoiCustomization, SegmentationModel, SEGMENTATION_MODEL_TYPE, SUPPORTED_PHYSICAL_PROPERTIES } from "./model-customization-types";
import { TreeViewIconType, getIconByType } from "./icon-helper";

/** JSON attribute that is inserted into outgoing customization JSON to make help identifying error message target components easier. */
export const UI_ID_ATTRIBUTE = 'ui_id';

const MODEL_VISIBILITY_JSON_VALUE_DEFAULT = 'default';
const MODEL_VISIBILITY_JSON_VALUE_ALWAYS_SHOW = 'always_show';
const MODEL_VISIBILITY_JSON_VALUE_ALWAYS_HIDE = 'always_hide';

/** Enables (or disables) customization tree view behaviour where selecting a branch item (such as a model name, e.g. 'Breast + Abdomen CT' or
 * a customization name, e.g. 'default') select the leaf node (e.g. 'mv-rt.dcm'). This will also have an effect on certain corner cases of what
 * the customization sidebar drawer remembers from the tree view selection when the drawer is collapsed and re-expanded.
 */
export const ENABLE_TREE_VIEW_BRANCHES_SELECT_LEAF_ITEM_BEHAVIOUR = false;

export const createNewModel = (modelName: string, label: string, bodyPart: string, description: string, visibility: ModelVisibility, customizationIds: string[], id?: string,): SegmentationModel => {
    return {
        id: id || generateNewId(),
        modelName: modelName,
        label: label,
        bodyPart: bodyPart,
        description: description,
        visibility: visibility,
        customizations: customizationIds,
        // TODO: add model_type support
        isModified: false,
        isAvailable: true,
        isDeprecated: false,
    };
};

export const createNewModelCustomizationBase = (customizationName: string, description: string, outputIds: string[], aeTitleRuleIds: string[], dicomRuleIds: string[], modelId: string, id?: string, isModified?: boolean): ModelCustomizationBase => ({
    id: id || generateNewId(),
    customizationName: customizationName,
    description: description,
    outputs: outputIds,
    aeTitleRules: aeTitleRuleIds,
    dicomRules: dicomRuleIds,
    segmentationModelId: modelId,
    isModified: isModified || false,
});

export const createNewModelCustomizationOutput = (metadataIds: string[], roiIds: string[], customizationBaseId: string, filename?: string, id?: string, isModified?: boolean): ModelCustomizationOutput => ({
    id: id || generateNewId(),
    metadata: metadataIds,
    rois: roiIds,
    modelCustomizationBaseId: customizationBaseId,
    filename: filename || '',
    isModified: isModified || false,
});

export const createNewMetadataItem = (attribute: string, value: string, modelCustomizationOutputId?: string): ModelCustomizationMetadataItem => ({
    id: generateNewId(),
    attribute: attribute,
    value: value,
    isModified: false,
    modelCustomizationOutputId: modelCustomizationOutputId,
    scrollToView: false,
});

export const createNewRoiCustomization = (operation: string, name: string, color: [number, number, number],
    interpretedType: string, physicalPropertyAttribute: PhysicalProperty, physicalPropertyValue: string,
    fmaId: FmaId, isIncluded: boolean, canBeDeleted: boolean, modelCustomizationOutputId?: string,
    id?: string, isModified?: boolean, scrollToView?: boolean): RoiCustomization => ({
        id: id || generateNewId(),
        operation: operation,
        name: name,
        color: color,
        interpretedType: interpretedType,
        physicalPropertyAttribute: physicalPropertyAttribute,
        physicalPropertyValue: physicalPropertyValue,
        ...fmaId,
        isIncluded: isIncluded,
        isBuiltInRoi: canBeDeleted === false,
        isModified: isModified || false,
        modelCustomizationOutputId: modelCustomizationOutputId,
        scrollToView: scrollToView || false,
    });

const createNewFmaId = (codeValue: string, codeMeaning: string, codingSchemeDesignator: string, codingSchemeVersion: string,
    mappingResource: string, contextGroupVersion: string, contextIdentifier: string, contextUid: string, mappingResourceUid: string,
    mappingResourceName: string): FmaId => ({
        hasFmaId: true,
        fmaIdCodeValue: codeValue,
        fmaIdCodeMeaning: codeMeaning,
        fmaIdCodingSchemeDesignator: codingSchemeDesignator,
        fmaIdCodingSchemeVersion: codingSchemeVersion,
        fmaIdMappingResource: mappingResource,
        fmaIdContextGroupVersion: contextGroupVersion,
        fmaIdContextIdentifier: contextIdentifier,
        fmaIdContextUid: contextUid,
        fmaIdMappingResourceUid: mappingResourceUid,
        fmaIdMappingResourceName: mappingResourceName,
    });

export const createNewAeTitleRule = (action?: string, subscription?: string,
    isEditable?: boolean, id?: string, modelCustomizationBaseId?: string, isNew?: boolean, isModified?: boolean,): AeTitleRule => ({
        id: id || generateNewId(),
        action: action || '',
        modelCustomizationBaseId: modelCustomizationBaseId || undefined,
        subscription: subscription || DEFAULT_SUBSCRIPTION,
        isEditable: isEditable || true,
        isModified: isModified || false,
        isNew: isNew || false,
        isValid: true,
        validationMessage: undefined,
    });

export const createNewDicomRule = (dicomAttributeIds: string[], id?: string, modelCustomizationBaseId?: string,
    subscription?: string, isNew?: boolean, isModified?: boolean): DicomRule => ({
        id: id || generateNewId(),
        modelCustomizationBaseId: modelCustomizationBaseId || undefined,
        dicomAttributes: dicomAttributeIds,
        subscription: subscription || DEFAULT_SUBSCRIPTION,
        isEditable: true,
        isModified: isModified || false,
        isNew: isNew || false,
        isValid: true,
        validationMessage: undefined,
    });

export const createNewDicomAttributeRule = (attribute: string, value: string, parentDicomRuleId: string, isNew?: boolean, id?: string): DicomAttributeRule => ({
    id: id || generateNewId(),
    attribute: attribute,
    value: value,
    parentDicomRuleId: parentDicomRuleId,
    isNew: isNew || false,
    isModified: false,
    isValid: true,
});

/**
 * Generates a new GlobalRoiCustomization object.
 * @param coveredRois array of matching RoiCustomizations that this global roi covers
 * @param excludedRois array of RoiCustomizations that have the same operation as this global roi but do not otherwise match with this one
 * @param noCoveredRoisOp If false or undefined, set the input covered rois' globalRoiId property to match this new object. 
 * If true, don't set it. If you call this function from redux store you probably want to set this to true and do any prop changing to the
 * original roi objects manually.
 */
export const createGlobalRoiCustomization = (coveredRois: RoiCustomization[], excludedRois: RoiCustomization[], noCoveredRoisOp?: boolean): GlobalRoiCustomization => {
    const globalRoi: GlobalRoiCustomization = {
        id: generateNewId(),
        operation: coveredRois[0].operation,
        name: coveredRois[0].name,
        isIncluded: coveredRois[0].isIncluded,
        color: coveredRois[0].color,
        interpretedType: coveredRois[0].interpretedType,
        physicalPropertyAttribute: coveredRois[0].physicalPropertyAttribute,
        physicalPropertyValue: coveredRois[0].physicalPropertyValue,
        ...getFmaId(coveredRois[0]),
        isModified: false,
        isBuiltInRoi: coveredRois.some(r => r.isBuiltInRoi === true),
        coveredRois: coveredRois.map(r => r.id),
        excludedRois: excludedRois.map(r => r.id)
    };
    if (!noCoveredRoisOp) { coveredRois.forEach(cr => cr.globalRoiId = globalRoi.id); }
    return globalRoi;
};

/** Extracts a (new) FmaId object out of a ROI or a Global ROI customization object. */
export const getFmaId = (roi: RoiCustomization | GlobalRoiCustomization): FmaId => {
    return {
        hasFmaId: roi.hasFmaId,
        fmaIdCodeValue: roi.fmaIdCodeValue,
        fmaIdCodeMeaning: roi.fmaIdCodeMeaning,
        fmaIdCodingSchemeDesignator: roi.fmaIdCodingSchemeDesignator,
        fmaIdCodingSchemeVersion: roi.fmaIdCodingSchemeVersion,
        fmaIdMappingResource: roi.fmaIdMappingResource,
        fmaIdContextGroupVersion: roi.fmaIdContextGroupVersion,
        fmaIdContextIdentifier: roi.fmaIdContextIdentifier,
        fmaIdContextUid: roi.fmaIdContextUid,
        fmaIdMappingResourceUid: roi.fmaIdMappingResourceUid,
        fmaIdMappingResourceName: roi.fmaIdMappingResourceName,
    }
}

/**
 * Cleans an FmaId object by removing keys with empty or whitespace-only string values.
 * @param fmaId The FmaId object to clean.
 * @returns The cleaned FmaId object or null if all fields are empty or if the input is null.
 */
export const cleanFmaId = (fmaId: any | null): any | null => {
    if (fmaId === null) {
        return null;
    }

    const cleanedFmaId: any = {};
    for (const key in fmaId) {
        if (fmaId.hasOwnProperty(key)) {
            const value = fmaId[key];
            if (typeof value === 'string' && trim(value) !== '') {
                cleanedFmaId[key] = value;
            }
        }
    }

    return isEmpty(cleanedFmaId) ? null : cleanedFmaId;
};
export const getDefaultFmaId = (): FmaId => ({ hasFmaId: false });

/** Convert model customization JSON from segmentation backend API into flattened internal view model representation.
 * 
 * Regarding sorting: if any sorting is done, it's generally done to try to ensure order by original
 * name using naturalSort. Dynamic sorting during user operation is generally not wanted as it could
 * erratically shift the position of the element on the page. Set the 'noSorting' prop to 'true' if sorting
 * is not wanted (e.g. for roundtrip unit tests).
 */
export const convertJsonObjectToModelCustomizations = (jsonObject: any, noSorting: boolean = false): ModelCustomizationEntities => {

    if (!jsonObject || !isObject(jsonObject)) {
        throw new Error('Invalid JSON object provided for Model Customizations conversion.');
    }

    const modelCollection: SegmentationModel[] = [];
    const customizationBaseCollection: ModelCustomizationBase[] = [];
    const customizationOutputCollection: ModelCustomizationOutput[] = [];
    const modelCustomizationsMetadataCollection: ModelCustomizationMetadataItem[] = [];
    const roiCustomizationCollection: RoiCustomization[] = [];
    const aeTitleRuleCollection: AeTitleRule[] = [];
    const dicomRuleCollection: DicomRule[] = [];
    const dicomAttributeRuleCollection: DicomAttributeRule[] = [];
    const globalRoiCustomizationCollection: GlobalRoiCustomization[] = [];

    const assertMessage = 'Unexpected segmentation model/structure customization format.';

    // convert json into view model objects
    forEach(jsonObject, (modelEntry: any) => {

        convertModelJsonToViewModelObjects(
            modelEntry,
            dicomAttributeRuleCollection,
            modelCustomizationsMetadataCollection,
            roiCustomizationCollection,
            customizationOutputCollection,
            customizationBaseCollection,
            aeTitleRuleCollection,
            dicomRuleCollection,
            modelCollection,
            assertMessage,
            noSorting);
    });


    // collect similar rois into GlobalRoiCustomization groups
    const groupedRois = groupBy(roiCustomizationCollection, roi => roi.operation);

    for (const roiCustomizations of Object.values(groupedRois)) {
        const hashedCustomizations: { [hash: string]: RoiCustomization[] } = {};
        for (const roiCustomization of roiCustomizations) {
            // find the largest group of similar roi customizations for each specific roi -- use hashes to find identical customizations
            const hashedRoi = generateRoiCustomizationHash(roiCustomization);
            hashedCustomizations[hashedRoi] = hashedCustomizations[hashedRoi] || [];
            hashedCustomizations[hashedRoi].push(roiCustomization);
        }
        const largestGroupHash = maxBy(Object.keys(hashedCustomizations), hash => hashedCustomizations[hash].length);
        if (largestGroupHash === undefined) { throw new Error('Could not find hash for largest group for roi'); }
        const largestGroupRois = hashedCustomizations[largestGroupHash];
        const largestGroupRoiIds = largestGroupRois.map(r => r.id);
        const excludedRois = roiCustomizations.filter(r => !largestGroupRoiIds.includes(r.id));
        const globalRoiCustomization = createGlobalRoiCustomization(largestGroupRois, excludedRois);
        globalRoiCustomizationCollection.push(globalRoiCustomization);
    }


    return {
        models: modelCollection,
        customizationBases: customizationBaseCollection,
        customizationOutputs: customizationOutputCollection,
        modelCustomizationsMetadata: modelCustomizationsMetadataCollection,
        roiCustomizations: roiCustomizationCollection,
        aeTitleRules: aeTitleRuleCollection,
        dicomRules: dicomRuleCollection,
        dicomAttributeRules: dicomAttributeRuleCollection,
        globalRoiCustomizations: naturalSort(globalRoiCustomizationCollection, 'name'),
    }
}

/** Convert model customization DTO/JSON containing just ONE model customization from segmentation backend 
 * API into flattened internal view model representation. Returns matching view model objects but does NOT
 * generate matching global ROI objects.
 * 
 * Regarding sorting: if any sorting is done, it's generally done to try to ensure order by original
 * name using naturalSort. Dynamic sorting during user operation is generally not wanted as it could
 * erratically shift the position of the element on the page. Set the 'noSorting' prop to 'true' if sorting
 * is not wanted (e.g. for roundtrip unit tests).
 */
export const convertSingleModelJsonToModelCustomizationOutput = (jsonObject: any, noSorting: boolean = false): ModelCustomizationOutputEntities => {
    if (!jsonObject || !isObject(jsonObject)) {
        throw new Error('Invalid JSON object provided for Model Customizations conversion.');
    }

    const modelCollection: SegmentationModel[] = [];
    const customizationBaseCollection: ModelCustomizationBase[] = [];
    const customizationOutputCollection: ModelCustomizationOutput[] = [];
    const modelCustomizationsMetadataCollection: ModelCustomizationMetadataItem[] = [];
    const roiCustomizationCollection: RoiCustomization[] = [];
    const aeTitleRuleCollection: AeTitleRule[] = [];
    const dicomRuleCollection: DicomRule[] = [];
    const dicomAttributeRuleCollection: DicomAttributeRule[] = [];

    const assertMessage = 'Unexpected segmentation model/structure customization format.';

    convertModelJsonToViewModelObjects(
        jsonObject,
        dicomAttributeRuleCollection,
        modelCustomizationsMetadataCollection,
        roiCustomizationCollection,
        customizationOutputCollection,
        customizationBaseCollection,
        aeTitleRuleCollection,
        dicomRuleCollection,
        modelCollection,
        assertMessage,
        noSorting);

    return {
        customizationOutputs: customizationOutputCollection,
        modelCustomizationsMetadata: modelCustomizationsMetadataCollection,
        roiCustomizations: roiCustomizationCollection,
    }
}

/** Converts a single JSON model object into matching view model objects */
function convertModelJsonToViewModelObjects(
    modelEntry: any,
    dicomAttributeRuleCollection: DicomAttributeRule[],
    modelCustomizationsMetadataCollection: ModelCustomizationMetadataItem[],
    roiCustomizationCollection: RoiCustomization[],
    customizationOutputCollection: ModelCustomizationOutput[],
    customizationBaseCollection: ModelCustomizationBase[],
    aeTitleRuleCollection: AeTitleRule[],
    dicomRuleCollection: DicomRule[],
    modelCollection: SegmentationModel[],
    assertMessage: string,
    noSorting: boolean) {

    // prepare a segmentation model id
    const modelId = generateNewId();

    // A temporary collection where model triggers (ae title rules and dicom rules) are stored during JSON-to-ViewModel conversion
    // and are linked by the customization names that they belong to. This is to make linking model triggers a bit later to their
    // matching CustomizationBase objects easier, as the parent customizationName field itself is NOT stored in any of the model
    // trigger-related view model objects.
    const modelTriggersByCustomizationNames: { [customizationName: string]: ModelTrigger[]; } = {};

    // assert that the base model stuff (element root items) looks correct
    assertExpectedProps(modelEntry, [
        { propName: 'model_name', propType: 'string' },
        { propName: 'model_label', propType: 'string' },
        { propName: 'model_type', propType: 'string' },
        { propName: 'body_part', propType: 'string' },
        { propName: 'description', propType: 'string' },
        { propName: 'customizations', propType: 'array' },
        { propName: 'triggers', propType: 'array' },
    ], assertMessage);

    const modelName = modelEntry['model_name'];
    const modelType = modelEntry['model_type'];

    // currently only segmentation models are supported
    if (modelType !== SEGMENTATION_MODEL_TYPE) {
        throw new Error(`Unexpected model type encountered (${modelType}) -- only segmentation models (${SEGMENTATION_MODEL_TYPE}) are supported`);
    }


    // extract selection triggers
    const triggers: ModelTrigger[] = modelEntry['triggers'].map((triggerEntry: any) => {
        // assert that trigger rule entries look correct
        assertExpectedProps(triggerEntry, [
            { propName: 'customization_name', propType: 'string' },
            { propName: 'subscription', propType: 'string' },
            { propName: 'action', propType: 'string' },
        ], assertMessage);

        const customizationName = triggerEntry['customization_name'];
        const subscription = triggerEntry['subscription'];
        const action = triggerEntry['action'];

        // prepare temporary model trigger collection for this customization name
        if (!modelTriggersByCustomizationNames[customizationName]) {
            modelTriggersByCustomizationNames[customizationName] = [];
        }

        // const matchingModelCustomizationBaseId = customizations.find(m => m.customizationName === customizationName)?.id;
        // if (matchingModelCustomizationBaseId === undefined) {
        //     throw new Error(`Could not find a matching customization '${customizationName}' for trigger rule`);
        // }
        if (action === DICOM_RULE_TRIGGER_ACTION && triggerEntry['dicom_attributes'] !== undefined) {
            // this is a DICOM attribute trigger
            // pre-generate dicom rule ID as we need it for dicom attribute rules
            const dicomRuleId = generateNewId();

            // assert that DICOM-specific trigger rule entries look correct
            assertExpectedProps(triggerEntry, [
                { propName: 'dicom_attributes', propType: 'object' },
            ], assertMessage);

            // collect dicom attribute rules
            const dicomAttributeRules = Object.entries(triggerEntry['dicom_attributes']).map(kvp => {
                const key = kvp[0];
                const value = kvp[1];
                if (!isString(value)) {
                    const message = `Unexpected type in received JSON: expected DICOM Attribute Rule key-value pairs to be all strings.`;
                    console.error(message);
                    console.log(`${key}: ${value}`);
                    console.log(triggerEntry);
                    throw new Error(message);
                }
                const dicomAttributeRule = createNewDicomAttributeRule(key, value, dicomRuleId);
                return dicomAttributeRule;
            });

            const dicomAttributeRuleIds = dicomAttributeRules.map(d => d.id);
            const dicomRule = createNewDicomRule(dicomAttributeRuleIds, dicomRuleId, undefined, subscription);

            // add newly created objects into collections that will eventually be put into redux store
            dicomAttributeRuleCollection.push(...dicomAttributeRules);
            modelTriggersByCustomizationNames[customizationName].push(dicomRule);

            return dicomRule;

        } else {
            // this is an AE title trigger
            const action = triggerEntry['action'];
            const isEditable = action !== customizationName; // triggers matching exact model names are hardcoded and cannot be edited
            const aeTitleRule = createNewAeTitleRule(action, subscription, isEditable);
            modelTriggersByCustomizationNames[customizationName].push(aeTitleRule);
            return aeTitleRule;
        }
    });


    // extract model customizations
    const customizations: ModelCustomizationBase[] = modelEntry['customizations'].map((modelCustomizationBaseEntry: any) => {

        // prepare a customization base id
        const customizationId = generateNewId();

        // assert that customization entries look correct
        assertExpectedProps(modelCustomizationBaseEntry, [
            { propName: 'customization_name', propType: 'string' },
            { propName: 'description', propType: 'string' },
            { propName: 'files', propType: 'array' },
        ], assertMessage);

        const customizationName = modelCustomizationBaseEntry['customization_name'];
        const customizationDescription = modelCustomizationBaseEntry['description'];

        // each 'file' will become its own customization output object
        const outputs: ModelCustomizationOutput[] = modelCustomizationBaseEntry['files'].map((customizationOutputEntry: any) => {

            // assert that output entries look correct
            assertExpectedProps(customizationOutputEntry, [
                { propName: 'metadata', propType: 'array' },
                { propName: 'rois', propType: 'array' },
            ], assertMessage);

            // extract metadata
            const metadata: ModelCustomizationMetadataItem[] = customizationOutputEntry['metadata'].map((metadataEntry: any) => {
                // assert that metadata entries look correct
                assertExpectedProps(metadataEntry, [
                    { propName: 'attribute', propType: 'string' },
                    { propName: 'value', propType: 'string' },
                ], assertMessage);

                // remove python string begin and end blocks from the string if found ("f'" and "'", e.g. "f'{model_name}'" becomes "{model_name}")
                const metadataAttribute = metadataEntry['attribute'];
                const metadataValue: string = metadataEntry['value'];
                let parsedMetadataValue = metadataValue.length >= 3 && metadataValue.startsWith("f'") && metadataValue.endsWith("'")
                    ? metadataValue.substring(2, metadataValue.length - 1)
                    : metadataValue;

                return createNewMetadataItem(metadataAttribute, parsedMetadataValue);
            });

            // extract rois & fma ids
            const rois: RoiCustomization[] = customizationOutputEntry['rois'].map((roiEntry: any) => {
                // assert that metadata entries look correct
                assertExpectedProps(roiEntry, [
                    { propName: 'operation', propType: 'string' },
                    { propName: 'name', propType: 'string' },
                    { propName: 'color', propType: 'rgbArray' },
                    { propName: 'interpreted_type', propType: 'string' },
                    { propName: 'can_be_deleted', propType: 'boolean' },
                    { propName: 'included', propType: 'boolean' },
                ], assertMessage);

                const fmaIdEntry = roiEntry['fma_id'];
                const parsedFmaId = fmaIdEntry ? createNewFmaId(
                    fmaIdEntry['code_value'],
                    fmaIdEntry['code_meaning'],
                    fmaIdEntry['coding_scheme_designator'],
                    fmaIdEntry['coding_scheme_version'],
                    fmaIdEntry['mapping_resource'],
                    fmaIdEntry['context_group_version'],
                    fmaIdEntry['context_identifier'],
                    fmaIdEntry['context_uid'],
                    fmaIdEntry['mapping_resource_uid'],
                    fmaIdEntry['mapping_resource_name']
                ) : { hasFmaId: false };

                let physicalPropertyAttribute = PhysicalProperty.NotSet;
                let physicalPropertyValue = '';
                const physicalPropertiesEntry = roiEntry['physical_properties'];
                if (physicalPropertiesEntry && isArray(physicalPropertiesEntry)) {
                    const matchingEntry = physicalPropertiesEntry.find(e => isString(e['physical_property']) && SUPPORTED_PHYSICAL_PROPERTIES.includes(e['physical_property']));
                    if (matchingEntry) {
                        physicalPropertyAttribute = matchingEntry['physical_property'] === PHYSICAL_PROPERTY_REL_ELEC_DENSITY ? PhysicalProperty.RelElecDensity : PhysicalProperty.RelMassDensity;
                        physicalPropertyValue = matchingEntry['value'].toString() || '';
                    }
                }

                return createNewRoiCustomization(roiEntry['operation'],
                    roiEntry['name'],
                    roiEntry['color'],
                    roiEntry['interpreted_type'],
                    physicalPropertyAttribute,
                    physicalPropertyValue,
                    parsedFmaId,
                    roiEntry['included'],
                    roiEntry['can_be_deleted']);
            });

            // collect ids. ensure roi id order within models
            const metadataIds = metadata.map(m => m.id);
            const roiIds = noSorting ? rois.map(r => r.id) : naturalSort(rois, 'name').map(r => r.id);
            const filename = metadata.find(m => m.attribute === METADATA_FILENAME_ATTRIBUTE)?.value;

            const output = createNewModelCustomizationOutput(metadataIds, roiIds, customizationId, filename);

            // update parent ids to child objects
            metadata.forEach(m => m.modelCustomizationOutputId = output.id);
            rois.forEach(r => r.modelCustomizationOutputId = output.id);

            // add newly created objects into collections that will eventually be put into redux store
            modelCustomizationsMetadataCollection.push(...metadata);
            roiCustomizationCollection.push(...rois);

            return output;
        });

        const outputIds = outputs.map(o => o.id);
        const matchingTriggers = modelTriggersByCustomizationNames[customizationName] || [];
        const aeTitleRules = matchingTriggers.filter(isAeTitleRule);
        const dicomRules = matchingTriggers.filter(isDicomRule);
        const aeTitleRuleIds = aeTitleRules.map(t => t.id);
        const dicomRuleIds = dicomRules.map(t => t.id);
        const customization = createNewModelCustomizationBase(customizationName, customizationDescription, outputIds, aeTitleRuleIds, dicomRuleIds, modelId, customizationId);

        // update matching customization IDs now that we have a customization object
        matchingTriggers.forEach(t => t.modelCustomizationBaseId = customization.id);

        // add newly created objects into collections that will eventually be put into redux store
        customizationOutputCollection.push(...outputs);

        return customization;
    });

    const customizationIds = customizations.map(c => c.id);

    // add newly created objects into collections that will eventually be put into redux store
    customizationBaseCollection.push(...customizations);
    aeTitleRuleCollection.push(...triggers.filter(isAeTitleRule));
    dicomRuleCollection.push(...triggers.filter(isDicomRule));

    const model = createNewModel(modelName, modelEntry['model_label'], modelEntry['body_part'], modelEntry['description'], parseModelVisibility(modelEntry['visibility']), customizationIds, modelId);

    modelCollection.push(model);
}


/** Convert internal view model representation of configuration objects into a model customization JSON hierarchy. */
export const convertModelCustomizationsToJson = (customizationEntities: ModelCustomizationEntitiesForExport, noUiId: boolean = false): any => {
    const jsonObject: any[] = [];

    const errorMessage = 'Could not find object matching given ID -- store is in inconsistent state';

    const {
        models,
        customizationBases,
        customizationOutputs,
        modelCustomizationsMetadata,
        roiCustomizations,
        aeTitleRules,
        dicomRules,
        dicomAttributeRules
    } = customizationEntities;

    for (const model of models) {
        const jsonModel: any = {
            'model_name': model.modelName,
            'model_label': model.label,
            'model_type': SEGMENTATION_MODEL_TYPE,
            'body_part': model.bodyPart,
            'description': model.description,
            'visibility': getModelVisibilityForJson(model.visibility),
            'customizations': [],
            'triggers': [],
        };
        if (!noUiId) { jsonModel[UI_ID_ATTRIBUTE] = model.id }

        // collect customizations
        for (const customizationBaseId of model.customizations) {
            const customizationBase = customizationBases.find(c => c.id === customizationBaseId);
            if (customizationBase === undefined) { throw new Error(errorMessage); }
            const jsonCustomization: any = {
                'customization_name': customizationBase.customizationName.trim(),
                'description': customizationBase.description.trim(),
                'files': [],
            };
            if (!noUiId) { jsonCustomization[UI_ID_ATTRIBUTE] = customizationBase.id }

            // collect triggers
            for (const aeTitleRuleId of customizationBase.aeTitleRules) {
                const aeTitleRule = aeTitleRules.find(ae => ae.id === aeTitleRuleId);
                if (aeTitleRule === undefined) { throw new Error(errorMessage); }

                const trigger: any = {
                    'customization_name': customizationBase.customizationName.trim(),
                    'subscription': aeTitleRule.subscription.trim(),
                    'action': aeTitleRule.action.trim(),
                };
                if (!noUiId) { trigger[UI_ID_ATTRIBUTE] = aeTitleRule.id }
                jsonModel['triggers'].push(trigger);
            }
            for (const dicomRuleId of customizationBase.dicomRules) {
                const dicomRule = dicomRules.find(d => d.id === dicomRuleId);
                if (dicomRule === undefined) { throw new Error(errorMessage); }

                const jsonDicomAttributes: any = {};
                for (const dicomAttributeId of dicomRule.dicomAttributes) {
                    const dicomAttribute = dicomAttributeRules.find(d => d.id === dicomAttributeId);
                    if (dicomAttribute === undefined) { throw new Error(errorMessage); }

                    // trim inside dicom regex value
                    const dicomValue = dicomAttribute.value.trim();
                    const trimmedDicomValue = dicomValue.startsWith('^') && dicomValue.endsWith('$') ? `^${dicomValue.slice(1, -1).trim()}$` : dicomValue;

                    jsonDicomAttributes[dicomAttribute.attribute.trim()] = trimmedDicomValue;
                }

                const trigger: any = {
                    'customization_name': customizationBase.customizationName.trim(),
                    'subscription': dicomRule.subscription.trim(),
                    'action': DICOM_RULE_TRIGGER_ACTION,
                    'dicom_attributes': jsonDicomAttributes,
                };
                if (!noUiId) { trigger[UI_ID_ATTRIBUTE] = dicomRule.id }
                jsonModel['triggers'].push(trigger);
            }


            // collect customization outputs
            for (const outputId of customizationBase.outputs) {
                const output = customizationOutputs.find(o => o.id === outputId);
                if (output === undefined) { throw new Error(errorMessage); }
                const jsonOutput: any = {
                    'metadata': [],
                    'rois': [],
                };
                if (!noUiId) { jsonOutput[UI_ID_ATTRIBUTE] = output.id }

                for (const metadataId of output.metadata) {
                    const metadata = modelCustomizationsMetadata.find(m => m.id === metadataId);
                    if (metadata === undefined) { throw new Error(errorMessage); }

                    let metadataValue = metadata.value.trim();

                    // add python string blocks (f' and ') unless they're already in the string
                    const parsedMetadataValue = metadataValue.startsWith("f'") && metadataValue.endsWith("'")
                        ? metadataValue
                        : `f'${metadataValue}'`;

                    const jsonMetadata: any = {
                        'attribute': metadata.attribute.trim(),
                        'value': parsedMetadataValue.trim(),
                    };
                    if (!noUiId) { jsonMetadata[UI_ID_ATTRIBUTE] = metadata.id }
                    jsonOutput['metadata'].push(jsonMetadata);
                }

                for (const roiId of output.rois) {
                    const roi = roiCustomizations.find(r => r.id === roiId);
                    if (roi === undefined) { throw new Error(errorMessage); }

                    const fmaIdOutput = roi.hasFmaId ? {
                        'code_value': roi.fmaIdCodeValue?.trim(),
                        'code_meaning': roi.fmaIdCodeMeaning?.trim(),
                        'coding_scheme_designator': roi.fmaIdCodingSchemeDesignator?.trim(),
                        'coding_scheme_version': roi.fmaIdCodingSchemeVersion?.trim(),
                        'mapping_resource': roi.fmaIdMappingResource?.trim(),
                        'context_group_version': roi.fmaIdContextGroupVersion?.trim(),
                        'context_identifier': roi.fmaIdContextIdentifier?.trim(),
                        'context_uid': roi.fmaIdContextUid?.trim(),
                        'mapping_resource_uid': roi.fmaIdMappingResourceUid?.trim(),
                        'mapping_resource_name': roi.fmaIdMappingResourceName?.trim(),
                    } : null;

                    const cleanedFmaIdOutput = cleanFmaId(fmaIdOutput);

                    const physicalProperties = [];
                    if (roi.physicalPropertyAttribute !== PhysicalProperty.NotSet && roi.physicalPropertyValue) {
                        const physicalProperty = {
                            'physical_property': roi.physicalPropertyAttribute === PhysicalProperty.RelElecDensity ? PHYSICAL_PROPERTY_REL_ELEC_DENSITY : PHYSICAL_PROPERTY_REL_MASS_DENSITY,
                            'value': parseFloat(roi.physicalPropertyValue.trim()),
                        };
                        if (!isNaN(physicalProperty.value)) {
                            physicalProperties.push(physicalProperty);
                        }
                    }

                    const jsonRoi: any = {
                        'included': roi.isIncluded,
                        'name': roi.name.trim(),
                        'color': roi.color,
                        'interpreted_type': roi.interpretedType,
                        'can_be_deleted': !roi.isBuiltInRoi,
                        'operation': roi.operation.trim(),
                        'fma_id': cleanedFmaIdOutput,
                        'physical_properties': physicalProperties,
                    };
                    if (!noUiId) { jsonRoi[UI_ID_ATTRIBUTE] = roi.id }
                    jsonOutput['rois'].push(jsonRoi);
                }

                jsonCustomization['files'].push(jsonOutput);
            }

            jsonModel['customizations'].push(jsonCustomization);
        }

        jsonObject.push(jsonModel);
    }

    return jsonObject;
}

/** Parses model visibility enum for view models from a JSON value. */
const parseModelVisibility = (modelVisibilityJson: string | undefined): ModelVisibility => {
    if (!!modelVisibilityJson) {
        if (modelVisibilityJson === MODEL_VISIBILITY_JSON_VALUE_ALWAYS_SHOW) { return ModelVisibility.AlwaysShown; }
        else if (modelVisibilityJson === MODEL_VISIBILITY_JSON_VALUE_ALWAYS_HIDE) { return ModelVisibility.AlwaysHidden; }
        else if (modelVisibilityJson === MODEL_VISIBILITY_JSON_VALUE_DEFAULT) { return ModelVisibility.Default; }
        else { throw new Error(`Unsupported model visibility value: ${modelVisibilityJson}`); }
    }

    return ModelVisibility.Default;
}

/** Converts view model visibility enum into matching JSON value for backend. */
const getModelVisibilityForJson = (modelVisibility: ModelVisibility): string => {
    switch (modelVisibility) {
        case ModelVisibility.Default:
            return MODEL_VISIBILITY_JSON_VALUE_DEFAULT;
        case ModelVisibility.AlwaysShown:
            return MODEL_VISIBILITY_JSON_VALUE_ALWAYS_SHOW;
        case ModelVisibility.AlwaysHidden:
            return MODEL_VISIBILITY_JSON_VALUE_ALWAYS_HIDE;
        default:
            throw new Error('Unsupported model visibility value');
    }
}

export const generateRoiCustomizationHash = (roi: RoiCustomization | GlobalRoiCustomization): string => {
    // separate fma id value props from other keys
    const fmaIdValueKeys = ['fmaIdCodeValue', 'fmaIdCodeMeaning', 'fmaIdCodingSchemeDesignator', 'fmaIdCodingSchemeVersion', 'fmaIdMappingResource',
        'fmaIdContextGroupVersion', 'fmaIdContextIdentifier', 'fmaIdContextUid', 'fmaIdMappingResourceUid', 'fmaIdMappingResourceName'];

    // only these keys are used for checking similarity
    const supportedKeys = ['operation', 'name', 'color', 'interpretedType', 'hasFmaId', 'physicalPropertyAttribute', 'physicalPropertyValue', ...fmaIdValueKeys];

    // ignore all keys that return 'true' in the excludeKeys function below, i.e. exclude
    // 1. anything that's not in supportedKeys,
    // 2. fmaIdValueKeys if the actual matching values are undefined (this improves optional property support between roi and globalroi objects)
    return hash(roi, { excludeKeys: (key) => !supportedKeys.includes(key) || (fmaIdValueKeys.includes(key) && get(roi, key, undefined)) === undefined });
}

export const generateNewId = (): string => {
    return uuidv4();
}

/**
 * Returns true if given roi customization object essentially matches given global roi customization object, such that
 * the global roi customization covers this roi customization.
 * @param globalRoiUpdates An optional update object for global roi customization object can be given. Any values in this object
 * are used instead of the matching props in globalRoi. This is useful for testing matching during data updates where final updates
 * haven't been applied to the globalRoi object yet.
 */
export const roiCustomizationMatchesGlobalRoi = (roi: RoiCustomization, globalRoi: GlobalRoiCustomization, globalRoiUpdates?: Partial<GlobalRoiCustomization>): boolean => {
    if (globalRoiUpdates === undefined) {
        return generateRoiCustomizationHash(roi) === generateRoiCustomizationHash(globalRoi);
    } else {
        return generateRoiCustomizationHash(roi) === generateRoiCustomizationHash(Object.assign({}, globalRoi, globalRoiUpdates));
    }
}

export const duplicateRoiCustomization = (roi: RoiCustomization, outputId?: string): RoiCustomization => {
    return {
        id: generateNewId(),
        operation: roi.operation,
        name: roi.name,
        color: roi.color,
        interpretedType: roi.interpretedType,
        physicalPropertyAttribute: roi.physicalPropertyAttribute,
        physicalPropertyValue: roi.physicalPropertyValue,
        ...getFmaId(roi),
        isIncluded: roi.isIncluded,
        isBuiltInRoi: roi.isBuiltInRoi,
        isModified: true,
        modelCustomizationOutputId: outputId,
        scrollToView: false,
        globalRoiId: roi.globalRoiId,
    };
}

export const duplicateModelCustomizationMetadataItem = (metadata: ModelCustomizationMetadataItem, outputId?: string): ModelCustomizationMetadataItem => {
    return {
        id: generateNewId(),
        attribute: metadata.attribute,
        value: metadata.value,
        isModified: true,
        modelCustomizationOutputId: outputId,
        scrollToView: false,
    };
}

/** Helper function for convertTreeViewDataIntoItemHierarchy. Returns true if the item specified here
 * should be rendered and false if it (and its children) should be hidden.
 */
export const showCustomizationTreeViewDataItem = (
    item: CustomizationTreeViewDataItem,
    includeNonLicensedModels: boolean,
    includeDeprecatedModels: boolean,
    includeUserHiddenModels: boolean,
    ignoreValidity: boolean = false): boolean => {

    // always show items that are set as 'always shown'
    if (item.modelVisibility === ModelVisibility.AlwaysShown) { return true; }

    // always show items with errors so user can identify and fix them
    // 'ignoreValidity' is used when double-checking if the item would've been hidden
    // if it had been valid
    if (!ignoreValidity && !item.isValid) { return true; }

    // only show items set as 'always hide' if user has opted to show hidden items
    if (item.modelVisibility === ModelVisibility.AlwaysHidden) { return includeUserHiddenModels; }

    // don't show items that aren't available unless specific filtering setting is on
    if (!item.isAvailable && !includeNonLicensedModels) { return false; }

    // don't show deprecated items unless specific filtering setting is on
    if (item.isDeprecated && !includeDeprecatedModels) { return false; }

    return true;
}

/** Helper function for converting hierarchical customization data (as generated in a separate selector 'selectCustomizationTreeViewItems'
 * in selectors.tsx) into an object hierarchy that can be read by a Patternfly TreeView component.
 */
export const convertTreeViewDataIntoItemHierarchy = (
    customizationHierarchy: CustomizationTreeViewDataItem[],
    getTreeViewItemName: (item: CustomizationTreeViewDataItem, noLicenseWarning: string) => ReactNode,
    noLicenseWarning: string,
    headerItems: TreeViewDataItem[],
    includeNonLicensedModels: boolean,
    includeDeprecatedModels: boolean,
    includeUserHiddenModels: boolean
): TreeViewDataItem[] => {

    // change headerItems name when a file is modified using getTreeViewItemName function
    for (const headerItem of headerItems) {
        const customizationItem = customizationHierarchy.find(c => c.id === headerItem.id);
        if (customizationItem && customizationItem.isModified) {
            headerItem.name = getTreeViewItemName(customizationItem, noLicenseWarning);
        }
    }

    // generate treeview data from memoized customization item hierarchy
    const data: TreeViewDataItem[] = [...headerItems];

    for (const bodyPartItem of customizationHierarchy) {
        if (!showCustomizationTreeViewDataItem(bodyPartItem, includeNonLicensedModels, includeDeprecatedModels, includeUserHiddenModels)) {
            continue;
        }

        let bodyPartItemData = data.find(p => p.id === bodyPartItem.id);
        if (!bodyPartItemData) {
            bodyPartItemData = {
                name: getTreeViewItemName(bodyPartItem, noLicenseWarning),
                id: bodyPartItem.id,
                children: [],

            };
            data.push(bodyPartItemData);
        } else if (!bodyPartItemData.children) {
            bodyPartItemData.children = [];
        }

        for (const modelItem of bodyPartItem.children) {
            if (!showCustomizationTreeViewDataItem(modelItem, includeNonLicensedModels, includeDeprecatedModels, includeUserHiddenModels)) {
                continue;
            }

            const modelItemData: TreeViewDataItem = {
                name: getTreeViewItemName(modelItem, noLicenseWarning),
                id: modelItem.id,
                children: modelItem.children.length > 0 ? [] : undefined,
                icon: getIconByType(TreeViewIconType.Folder),
                expandedIcon: getIconByType(TreeViewIconType.Folder),
            };

            if (ENABLE_TREE_VIEW_BRANCHES_SELECT_LEAF_ITEM_BEHAVIOUR && modelItem.children.length > 0) {
                // put either the 'default' or first customization base as the open id for the segmentation model
                const defaultCustomization = modelItem.children.find(c => c.label === DEFAULT_MODEL_CUSTOMIZATION_NAME);
                modelItemData.id = defaultCustomization ? defaultCustomization.id : modelItem.children[0].id;
            }

            for (const baseItem of modelItem.children) {
                if (!showCustomizationTreeViewDataItem(baseItem, includeNonLicensedModels, includeDeprecatedModels, includeUserHiddenModels)) {
                    continue;
                }

                let baseItemData: TreeViewDataItem = {
                    name: getTreeViewItemName(baseItem, noLicenseWarning),
                    id: baseItem.id,
                    children: baseItem.children.length > 0 ? [] : undefined,
                    // always open file-level items
                    defaultExpanded: true,
                    icon: getIconByType(TreeViewIconType.Gear)
                };

                if (ENABLE_TREE_VIEW_BRANCHES_SELECT_LEAF_ITEM_BEHAVIOUR && baseItem.children.length > 0) {
                    // put first file in the list as the open id for the customization base
                    baseItemData.id = baseItem.children[0].id;

                    // also finish up setting the correct id for the segmentation model
                    if (modelItemData.id === baseItem.id) {
                        modelItemData.id = baseItem.children[0].id;
                    }
                }

                for (const outputItem of baseItem.children) {
                    if (!showCustomizationTreeViewDataItem(outputItem, includeNonLicensedModels, includeDeprecatedModels, includeUserHiddenModels)) {
                        continue;
                    }

                    const outputItemData: TreeViewDataItem = {
                        name: baseItem.children.length > 1 ? getTreeViewItemName(outputItem, noLicenseWarning) : baseItemData.name,
                        id: outputItem.id,
                        icon: baseItem.children.length > 1 ? getIconByType(TreeViewIconType.File) : getIconByType(TreeViewIconType.Gear),
                    };

                    // If the children is just 1 skip rendering the output item and instead assign the output item to the base Item
                    if (baseItem.children.length > 1) {
                        baseItemData.children!.push(outputItemData);
                    }
                    else {
                        baseItemData = outputItemData;
                    }
                }

                modelItemData.children!.push(baseItemData);
            }

            bodyPartItemData.children!.push(modelItemData);
        }
    }

    return data;
}

/**
 * Extracts the common FMA ID attributes from the given ROI customization.
 * Note: Only the common FMA ID attributes are returned.
 * @param roiCustomization The ROI customization object.
 * @returns The common FMA ID attributes, with some fields potentially undefined.
 */
export const extractSharedFmaIdAttributes = (roiCustomization: RoiCustomization | GlobalRoiCustomization, includeOutputId: boolean): Partial<FmaId> => {
    let extracted: Partial<FmaId> = {
        hasFmaId: roiCustomization.hasFmaId,
        fmaIdCodingSchemeDesignator: roiCustomization.fmaIdCodingSchemeDesignator,
        fmaIdCodingSchemeVersion: roiCustomization.fmaIdCodingSchemeVersion,
        fmaIdMappingResource: roiCustomization.fmaIdMappingResource,
        fmaIdContextGroupVersion: roiCustomization.fmaIdContextGroupVersion,
        fmaIdContextIdentifier: roiCustomization.fmaIdContextIdentifier,
        fmaIdContextUid: roiCustomization.fmaIdContextUid,
        fmaIdMappingResourceUid: roiCustomization.fmaIdMappingResourceUid,
        fmaIdMappingResourceName: roiCustomization.fmaIdMappingResourceName,
    };

    if (includeOutputId) {
        extracted.modelCustomizationOutputId = roiCustomization.modelCustomizationOutputId;
    }

    return extracted;
};



export const readModelCustomizationJsonFile = (json: any): Promise<ModelCustomizationEntities | null> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            try {
                const jsonObject = JSON.parse(reader.result as string);

                const modelCustomizationImport = convertJsonObjectToModelCustomizations(jsonObject);
                resolve(modelCustomizationImport);
            } catch (e) {
                reject(e);
            }
        };

        reader.onerror = (error) => {
            reject(error);
        };

        reader.readAsText(json);
    });
};
